import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';

import { SelectChangeEvent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import SidebarLayout from '../../layouts/SidebarLayout';
import { appointmentActions } from '../../store/appointment';
import { RootState } from '../../store';
import CustomDatePicker from '../../components/UI/CustomDatePicker/CustomDatePicker';
import { loaderActions } from '../../store/loader';
import MedicalCenterDropdown from './MedicalCenterDropdown';
import DoctorCardGrid from './DoctorCardGrid';
import CustomBottomButtonGroup from '../../components/UI/CustomBottomButtonGroup/CustomBottomButtonGroup';
import { Doctor } from '../../../domain/entities';
import { useDoctorService, useSpecialtyService } from '../../hooks';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const MedicalCenterDoctors = () => {
  const SpecialtyService = useSpecialtyService();
  const DoctorService = useDoctorService();
  const dispatch = useDispatch();
  const query = useQuery();
  const appointment = useSelector((state: RootState) => state.appointment);

  const [doctorList, setDoctorList] = useState<Doctor[]>([]);
  const [medicalCenter, setMedicalCenter] = useState('');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false);

  const isLoading = (state: boolean) => {
    dispatch(loaderActions.setLoading(state));
  };

  useEffect(() => {
    dispatch(appointmentActions.resetAppointment());
    SpecialtyService.getOne(query.get('specialty') ?? '')
      .then((specialty) => dispatch(appointmentActions.setSpecialty(specialty)))
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    if (!currentDate || medicalCenter.length === 0) return;

    isLoading(true);
    DoctorService.listForAppointment({
      specialtyCode: appointment.specialty.code,
      medicalCenter,
      currentDate
    })
      .then((data) => setDoctorList(data))
      .catch((e) => console.log(e))
      .finally(() => isLoading(false));
  }, [currentDate, medicalCenter]);

  const changeMedicalCenterHandler = (event: SelectChangeEvent<string>) => {
    const selectedMedicalCenter = event.target.value;
    setMedicalCenter(selectedMedicalCenter);
    dispatch(appointmentActions.resetDoctor());
    dispatch(appointmentActions.resetSchedule());
    dispatch(appointmentActions.setMedicalCenter(selectedMedicalCenter));
    setIsFormComplete(false);
  };

  const setDateHandler = (newValue: Date | null | undefined) => {
    if (!newValue) return;
    setCurrentDate(newValue);
  };

  const setScheduleHandler = (doctor: Doctor, schedule: Date) => {
    dispatch(
      appointmentActions.setDoctor({
        id: doctor.id,
        name: doctor.name
      })
    );
    dispatch(
      appointmentActions.setSchedule({
        datetimeInMilliseconds: schedule.getTime()
      })
    );
    setIsFormComplete(true);
  };

  return (
    <SidebarLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <h2>{appointment.specialty.name}</h2>
        </Box>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}
        >
          <MedicalCenterDropdown
            medicalCenter={appointment.medicalCenter}
            onMedicalCenterChanged={changeMedicalCenterHandler}
          />
          <CustomDatePicker value={currentDate} onChange={setDateHandler} disablePast />
        </Box>
        <DoctorCardGrid
          doctors={doctorList}
          medicalCenter={medicalCenter}
          selectedDate={currentDate}
          onScheduleSelected={setScheduleHandler}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            margin: 1,
            paddingTop: 3
          }}
        >
          <CustomBottomButtonGroup
            canContinue={isFormComplete}
            navigateTo={'/citas/especialidades/centros/crear'}
            isNavigation
          />
        </Box>
      </Box>
    </SidebarLayout>
  );
};

export default MedicalCenterDoctors;
